import lscache from 'lscache';
import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon, FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import { copyToClipboard } from '@scale/frontend-lib/clipboard';
import { push } from '@scale/frontend-lib/link';
import { withErrorBoundary } from 'react-scale/containers/global/errorBoundary';

interface IHideableSectionProps {
  name: string;
  nameStatusIcon?: { icon?: FontAwesomeIconProps['icon']; color: string };
  forceHidden?: boolean;
  children: React.ReactNode;
  showOverflow?: boolean;
  containZoom?: boolean;
  sectionId?: string;
  linterSelected?: boolean;
  enableLink?: boolean;
  keepMounted?: boolean; // Keep children mounted when hidden (just CSS hide)
}

const HideableSection: React.FC<IHideableSectionProps> = props => {
  const getCacheKey = (name: string): string => `hideablesection/${name}/hidden`;
  const [hidden, setHidden] = useState(
    props.forceHidden ?? lscache.get(getCacheKey(props.name)) ?? false,
  );
  const divRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  const enableLink = props.enableLink ?? true;
  useEffect(() => {
    setHidden(props.forceHidden ?? lscache.get(getCacheKey(props.name)) ?? false);
  }, [props.forceHidden, props.name]);

  useEffect(() => {
    if (divRef.current && window.location.hash === `#${encodeURIComponent(props.name)}`) {
      divRef.current.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
    }
    if (sectionRef.current && props.containZoom) {
      sectionRef.current.addEventListener('wheel', e => {
        if (e.ctrlKey) {
          e.preventDefault();
        }
      });
    }
  });

  useEffect(() => {
    if (props.linterSelected) toggleHidden();
  }, []);

  const scrollIntoView: React.MouseEventHandler<HTMLElement> = e => {
    e.stopPropagation();
    if (divRef.current) {
      divRef.current.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
      push(`${window.location.pathname}#${encodeURIComponent(props.name)}`, undefined, {
        shallow: true,
      });
    }
  };

  const copyUrl = () => {
    const urlAbsPath = window.location.href.split('#')[0];
    copyToClipboard(`${urlAbsPath}#${props.sectionId}`, { excludeOuterQuotes: true });
  };

  const toggleHidden = () => {
    const newHidden = !hidden;
    setHidden(newHidden);
    lscache.set(getCacheKey(props.name), newHidden);
  };

  if (!props.children) {
    return null;
  }

  return (
    <div ref={divRef} className="section">
      {props.sectionId ? (
        <div className="section-header" onClick={toggleHidden}>
          {/* onClick section is limited to the arrow icon */}
          <em style={{ minWidth: 24, paddingRight: 0 }}>
            <FontAwesomeIcon icon={hidden ? 'caret-right' : 'caret-down'} size="sm" />
          </em>
          {props.nameStatusIcon && (
            <FontAwesomeIcon
              style={{ paddingRight: '6px' }}
              icon={props.nameStatusIcon.icon || 'circle'}
              color={props.nameStatusIcon.color}
            />
          )}
          <div className="section-title">
            {props.name}
            {/* if sectionId, clipboard will save the location url */}
            <em
              onClick={event => {
                event.stopPropagation();
                copyUrl();
              }}
            >
              <FontAwesomeIcon icon="link" size="xs" />
            </em>
          </div>
        </div>
      ) : (
        <div className="section-header" onClick={toggleHidden}>
          <em style={{ minWidth: 24, paddingRight: 0 }}>
            <FontAwesomeIcon icon={hidden ? 'caret-right' : 'caret-down'} size="sm" />
          </em>
          {props.nameStatusIcon && (
            <FontAwesomeIcon
              style={{ paddingRight: '6px' }}
              icon={props.nameStatusIcon.icon || 'circle'}
              color={props.nameStatusIcon.color}
            />
          )}
          <div className="section-title">
            {props.name}
            {enableLink && (
              <em onClick={scrollIntoView} className="pl-3">
                <FontAwesomeIcon icon="link" size="xs" />
              </em>
            )}
          </div>
        </div>
      )}

      {props.keepMounted ? (
        <div
          className="section-body"
          ref={sectionRef}
          style={{ display: hidden ? 'none' : 'block' }}
        >
          {props.children}
        </div>
      ) : (
        !hidden && (
          <div className="section-body" ref={sectionRef}>
            {props.children}
          </div>
        )
      )}

      <style jsx>{`
        .section {
          margin: 16px 0;
          border: 1px solid rgba(0, 0, 0, 0.05);
          border-radius: var(--border-radius);
          ${props.showOverflow ? '' : 'overflow: hidden;'}
        }

        .section-header {
          padding: 12px 8px;
          font-weight: bold;
          font-size: 20px;
          cursor: pointer;
          user-select: none;
          transition: background 0.2s;
        }

        .section-header:hover {
          background: var(--color-RemoGray01);
        }

        .section-header:hover .section-title :global(path) {
          fill: var(--color-RemoGray20);
        }

        .section-header:not(:hover) .section-title :global(path) {
          fill: white;
        }

        .section-header .section-title {
          display: inline-block;
        }

        .section-header .section-title:hover :global(path) {
          fill: var(--color-RemoGray50) !important;
        }

        .section-header em {
          display: inline-block;
        }

        .section-body {
          padding: 16px;
        }
      `}</style>
    </div>
  );
};

export default withErrorBoundary({ reloadable: true })(HideableSection);
